import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { toast } from "react-toastify";
import AppDialog from "../../Components/Dialogs/AppDialog/AppDialog";
import { DashlyTextInput } from "../../Components/inputs";
import IconPicker from "../../Components/inputs/IconPicker/IconPicker";
import PrimeIcon from "../../Models/PrimeIcon";
import { IconService } from "../../Services/IconService";
import { IKnowledgeCategoryModel, KnowledgeCategoryService } from "../../Services/KnowledgeCategoryService";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import { DEFAULT_KNOWLEDGE_CATEGORY_ICON } from "./knowledgeBaseConstants";
import "./ManageKnowledgeCategoriesDialog.scss";

interface ManageKnowledgeCategoriesDialogProps {
    visible: boolean;
    categories: IKnowledgeCategoryModel[];
    onHide: () => void;
    /** Something was saved. {@code deletedUuids}: the categories that no longer exist. */
    onChanged: (deletedUuids: string[]) => void;
}

const pickerValue = (icon?: string): string => icon?.trim() || DEFAULT_KNOWLEDGE_CATEGORY_ICON;

const sameIcon = (left?: string, right?: string): boolean => (left ?? "") === (right ?? "");

const ManageKnowledgeCategoriesDialog: React.FC<ManageKnowledgeCategoriesDialogProps> = ({
    visible,
    categories,
    onHide,
    onChanged,
}) => {
    const { t } = useTranslation();
    const knowledgeCategoryService = useMemo(() => new KnowledgeCategoryService(), []);
    const iconService = useMemo(() => new IconService(), []);
    const [icons, setIcons] = useState<PrimeIcon[]>([]);
    // What the dialog shows. Adding, renaming and deleting only change these rows: nothing is sent before Save,
    // and Cancel leaves everything as it was. A row without an original is new, an original without a row is
    // deleted.
    const [rows, setRows] = useState<IKnowledgeCategoryModel[]>([]);
    const originalsRef = useRef<Record<string, { name: string; icon?: string }>>({});
    const categoriesRef = useRef(categories);
    categoriesRef.current = categories;
    const [newName, setNewName] = useState("");
    const [newIcon, setNewIcon] = useState<string | undefined>();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!visible) {
            return;
        }
        const snapshot = categoriesRef.current;
        setRows(snapshot.map((category) => ({ ...category })));
        originalsRef.current = Object.fromEntries(
            snapshot.map((category) => [category.uuid, { name: category.name, icon: category.icon }]),
        );
        setNewName("");
        setNewIcon(undefined);
    }, [visible]);

    useEffect(() => {
        if (!visible || icons.length > 0) {
            return;
        }
        iconService.getIcons().then(setIcons);
    }, [visible, icons.length, iconService]);

    const isNewRow = (row: IKnowledgeCategoryModel): boolean => !originalsRef.current[row.uuid];

    const dirtyRows = rows.filter((row) => {
        const original = originalsRef.current[row.uuid];
        if (!original) {
            return false;
        }
        return original.name !== row.name.trim() || !sameIcon(original.icon, row.icon);
    });
    const newRows = rows.filter(isNewRow);
    const removedUuids = Object.keys(originalsRef.current).filter((uuid) => !rows.some((row) => row.uuid === uuid));
    const canSave = dirtyRows.length > 0 || newRows.length > 0 || removedUuids.length > 0 || Boolean(newName.trim());

    const handleIconChange = (uuid: string, event: { value?: string } | string) => {
        const next = typeof event === "string" ? event : event?.value;
        const icon = next?.trim() || undefined;
        setRows((current) => current.map((item) => (item.uuid === uuid ? { ...item, icon } : item)));
    };

    /** The add row as a row of its own, under an id that only lives in this dialog. */
    const addRowAsCategory = (): IKnowledgeCategoryModel => ({
        uuid: crypto.randomUUID(),
        name: newName.trim(),
        icon: newIcon,
        displayOrder: 0,
        active: true,
    });

    const handleAdd = () => {
        if (!newName.trim()) {
            return;
        }
        setRows((current) => [...current, addRowAsCategory()]);
        setNewName("");
        setNewIcon(undefined);
    };

    const removeRow = (uuid: string) => setRows((current) => current.filter((item) => item.uuid !== uuid));

    const persistRow = async (row: IKnowledgeCategoryModel) => {
        const name = row.name.trim();
        await knowledgeCategoryService.putItem(row.uuid, {
            name,
            // Blank clears the icon; leaving it out would make the backend keep the old one.
            icon: row.icon?.trim() || "",
            displayOrder: row.displayOrder,
        });
        originalsRef.current[row.uuid] = { name, icon: row.icon };
    };

    /** From here on the row is an existing category: a second Save after a failure does not create it again. */
    const persistNewRow = async (row: IKnowledgeCategoryModel) => {
        const name = row.name.trim();
        const response = await knowledgeCategoryService.postItem({
            name,
            icon: row.icon?.trim() || undefined,
        });
        const newUuid = response.data.data as string;
        originalsRef.current[newUuid] = { name, icon: row.icon };
        setRows((current) => current.map((item) => (item.uuid === row.uuid ? { ...item, uuid: newUuid, name } : item)));
    };

    const handleSave = async () => {
        if (!canSave || saving) {
            return;
        }
        // A name still sitting in the add row is meant to be saved too.
        const typedRow = newName.trim() ? addRowAsCategory() : null;
        if (rows.some((row) => !row.name.trim())) {
            toast.error(t("knowledgeBase.category.saveError") as string);
            return;
        }
        if (typedRow) {
            setRows((current) => [...current, typedRow]);
            setNewName("");
            setNewIcon(undefined);
        }
        setSaving(true);
        const deletedUuids: string[] = [];
        let savedAnything = false;
        try {
            for (const uuid of removedUuids) {
                await knowledgeCategoryService.deleteItem(uuid);
                delete originalsRef.current[uuid];
                deletedUuids.push(uuid);
                savedAnything = true;
            }
            for (const row of dirtyRows) {
                await persistRow(row);
                savedAnything = true;
            }
            for (const row of typedRow ? [...newRows, typedRow] : newRows) {
                await persistNewRow(row);
                savedAnything = true;
            }
            toast.success(t("knowledgeBase.category.saveSuccess") as string);
            onChanged(deletedUuids);
            onHide();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.category.saveError", t));
            // What was saved before the failure is real; the rest is still here to save again.
            if (savedAnything) {
                onChanged(deletedUuids);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (category: IKnowledgeCategoryModel, target: HTMLElement) => {
        if (isNewRow(category)) {
            removeRow(category.uuid);
            return;
        }
        const label = category.name || (t("knowledgeBase.category.deleteFallbackLabel") as string);
        // The same anchored popup every list page uses (rendered once, in App).
        confirmPopup({
            target,
            message: t("processflow.list.deleteConfirm", { label }) as string,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("processflow.list.deleteAccept") as string,
            rejectLabel: t("processflow.list.deleteReject") as string,
            acceptClassName: "p-button-danger",
            accept: () => removeRow(category.uuid),
        });
    };

    return (
        <AppDialog
            visible={visible}
            onHide={onHide}
            header={t("knowledgeBase.category.manageTitle") as string}
            className="manage-knowledge-categories-dialog"
            style={{ width: "min(92vw, 36rem)" }}
            footerActions={{
                cancelLabel: t("common.action.cancel") as string,
                onCancel: onHide,
                cancelDisabled: saving,
                submitLabel: t("common.action.save") as string,
                onSubmit: handleSave,
                submitDisabled: !canSave || saving,
                submitLoading: saving,
            }}
        >
            <p className="manage-knowledge-categories-dialog__hint">{t("knowledgeBase.category.iconHint")}</p>
            {rows.length === 0 && (
                <p className="manage-knowledge-categories-dialog__empty">{t("knowledgeBase.category.empty")}</p>
            )}
            <div className="manage-knowledge-categories-dialog__list">
                {rows.map((row) => (
                    <div key={row.uuid} className="manage-knowledge-categories-dialog__row">
                        <IconPicker
                            className="manage-knowledge-categories-dialog__icon"
                            value={pickerValue(row.icon)}
                            icons={icons}
                            onChange={(event: { value?: string } | string) => handleIconChange(row.uuid, event)}
                            disabled={saving}
                        />
                        <DashlyTextInput
                            value={row.name}
                            onChange={(event) => {
                                const name = event.target.value;
                                setRows((current) =>
                                    current.map((item) => (item.uuid === row.uuid ? { ...item, name } : item)),
                                );
                            }}
                            placeholder={t("knowledgeBase.category.namePlaceholder") as string}
                            maxLength={100}
                            disabled={saving}
                            controlSize="sm"
                        />
                        <Button
                            type="button"
                            icon="pi pi-trash"
                            text
                            rounded
                            severity="danger"
                            aria-label={t("common.action.delete") as string}
                            disabled={saving}
                            onClick={(event) => handleDelete(row, event.currentTarget)}
                        />
                    </div>
                ))}
            </div>
            <div className="manage-knowledge-categories-dialog__row manage-knowledge-categories-dialog__row--add">
                <IconPicker
                    className="manage-knowledge-categories-dialog__icon"
                    value={pickerValue(newIcon)}
                    icons={icons}
                    onChange={(event: { value?: string } | string) => {
                        const next = typeof event === "string" ? event : event?.value;
                        setNewIcon(next?.trim() || undefined);
                    }}
                    disabled={saving}
                />
                <DashlyTextInput
                    value={newName}
                    onChange={(event) => setNewName(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            handleAdd();
                        }
                    }}
                    placeholder={t("knowledgeBase.category.namePlaceholder") as string}
                    maxLength={100}
                    disabled={saving}
                    controlSize="sm"
                />
                <Button
                    type="button"
                    icon="pi pi-plus"
                    text
                    rounded
                    aria-label={t("knowledgeBase.category.add") as string}
                    tooltip={t("knowledgeBase.category.add") as string}
                    tooltipOptions={{ position: "top" }}
                    onClick={handleAdd}
                    disabled={saving || !newName.trim()}
                />
            </div>
        </AppDialog>
    );
};

export default ManageKnowledgeCategoriesDialog;
